import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  TableHeadCell,
  TableIconCell,
  Pagination,
  Chip,
  ContainerAvatar,
  TableIconsCell,
  Button,
  TableCellWrapperCenter,
  TableDots,
  repairSetUpdateModal,
  enqueueSnackbar,
  repairClientConfirmModal,
  repairDeleteModal,
  repairCompleteModal,
  SimpleTable,
} from '../../design-system';
import { DashboardHeader } from '../dashboard';
import { Filters } from './Filters';
import { useStore, useStoreUser } from '../../context';
import { momentTextFormat, showToRoles, getRepairOptions } from '../../utils';
import {
  useStatesWithFilterCounter,
  useSort,
  usePaginationState,
  useRepairsQuery,
  useConvertCurrencyFcQuery,
} from '../../hooks';
import {
  TABLE,
  RepairStatusToChipLabels,
  RepairStatusToChipColors,
  CURRENCIES,
  MESSAGES,
  RepairInvoiceStatusToChipLabels,
  RepairInvoiceStatusToChipColors,
} from '../../const';
import { CurrencyCode, RepairGetAllQuery } from '../../types';
import { i18n } from '../../i18n';

interface RepairsPageProps {
  innerScroll?: boolean;
}

export const RepairsPage = styled.div<RepairsPageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

export const RepairsHeader = ({
  totalCount,
  totalPrice,
  handleRowClick,
  onAddRepairClick,
}: any) => {
  const { user } = useStoreUser();

  let title = i18n.t('pages.repairs.title', { totalCount });

  if (handleRowClick) {
    title = i18n.t('pages.repairs.titleWithTotalPrice', { totalCount, totalPrice });
  }

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {title}
      </Typography>
      {showToRoles('admin', user) && onAddRepairClick && (
        <Button size={'medium'} data-color={'primary'} onClick={onAddRepairClick}>
          {i18n.t('button.addRepair')}
        </Button>
      )}
    </DashboardHeader>
  );
};

export const RepairsBody = ({ setTotalCount, setTotalPrice, containerId, ...props }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const navigate = useNavigate();
  const { page, take, skip, handlePageChange } = usePaginationState();
  const [openDotsMenuIndex, setDotsMenuIndex] = useState(null);
  const [states, setStates, filteredStates] = useStatesWithFilterCounter({
    country: '',
    city: '',
    yardId: '',
    clientId: '',
    number: '',
    status: 'All',
  });
  const { sortField, sortDirection, handleSort } = useSort({
    sortField: 'createdAt',
    sortDirection: 'desc',
  });

  const convertCurrency = useConvertCurrencyFcQuery();
  const {
    data = { data: [], totalCount: 0 },
    isFetching,
    refetch,
  } = useRepairsQuery({
    ...filteredStates,
    take,
    skip,
    sortField,
    sortDirection,
    containerId,
  });

  useEffect(() => {
    if (isFetching) return;
    if (convertCurrency('USD', 'RUB', 1) === 1) return;

    let defaultCurrencyCode = 'USD' as CurrencyCode;

    if (containerId) {
      defaultCurrencyCode = data.data[0]?.currencyCode;
    }

    const allPrice = data.data.reduce((prev, curr) => {
      return prev + convertCurrency(curr.currencyCode, defaultCurrencyCode, curr.price);
    }, 0);
    const totalPrice = Number(allPrice.toFixed(2)) + CURRENCIES[defaultCurrencyCode]?.symbol;

    setTotalCount(data.totalCount);
    setTotalPrice(totalPrice || 0);
  }, [isFetching, refetch, convertCurrency('USD', 'RUB', 1) === 1]);

  const handleDotsToggle = (i: any) => {
    if (openDotsMenuIndex !== i) return setDotsMenuIndex(i);

    setDotsMenuIndex(null);
  };

  const handleOnPageChange = (e: any, page: number) => {
    handlePageChange(e, page);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handleOnPageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = (
    row: RepairGetAllQuery['data'][number],
    number: any,
    val: any,
    id: any,
  ) => {
    if (val === 'set-price' || val === 'edit') {
      repairSetUpdateModal.action = val;
      repairSetUpdateModal.number = number;

      repairSetUpdateModal.data = [
        {
          id,
          price: row.price ? String(row.price) : '0',
          currencyCode: row.currencyCode,
          expectedDurationDays: row.expectedDurationDays,
          description: row.description,
          beforeImages: row.beforeImages,
          afterImages: row.afterImages,
        },
      ];

      repairSetUpdateModal.submitClick = () => {
        enqueueSnackbar(MESSAGES.REPAIR.SUCCESS.UPDATED, 'success');
        refetch();
      };
      repairSetUpdateModal.open();
    }
    if (val === 'client-confirm' || val === 'client-reject') {
      repairClientConfirmModal.id = id;
      repairClientConfirmModal.isConfirm = val === 'client-confirm';
      repairClientConfirmModal.number = number;

      repairClientConfirmModal.submitClick = () => {
        const message =
          val === 'client-confirm'
            ? MESSAGES.REPAIR.SUCCESS.APPROVED
            : MESSAGES.REPAIR.SUCCESS.REJECTED;
        enqueueSnackbar(message, 'success');
        refetch();
      };
      repairClientConfirmModal.open();
    }
    if (val === 'complete') {
      repairCompleteModal.id = id;
      repairCompleteModal.number = number;
      repairCompleteModal.description = row.description;
      repairCompleteModal.beforeImages = row.beforeImages;

      repairCompleteModal.submitClick = () => {
        enqueueSnackbar(MESSAGES.REPAIR.SUCCESS.COMPLETED, 'success');
        refetch();
      };
      repairCompleteModal.open();
    }
    if (val === 'delete') {
      repairDeleteModal.id = id;
      repairDeleteModal.number = number;

      repairDeleteModal.submitClick = () => {
        enqueueSnackbar(MESSAGES.REPAIR.SUCCESS.DELETED, 'success');
        refetch();
      };
      repairDeleteModal.open();
    }
  };

  const handleRowClick = (to: string, data: any) => {
    if (openDotsMenuIndex !== null) return;

    if (props.handleRowClick) {
      props.handleRowClick(data);
    } else {
      navigate(to);
    }
  };

  return (
    <SimpleTable
      data={data}
      filters={!containerId ? () => <Filters setStates={handleFilterChange} {...states} /> : null}
      tableRowProps={(repair, i) => ({
        key: String(i),
        onClick: () =>
          handleRowClick(
            containerId ? `/dashboard/repairs/${containerId}` : `/dashboard/repairs/${repair.id}`,
            repair,
          ),
      })}
      columns={[
        {
          column: 1,
          head: () => <TableHeadCell align={'left'}>{i18n.t('label.actions')}</TableHeadCell>,
          body: (repair, i) => (
            <TableIconsCell component={'th'} align={'left'}>
              {!!getRepairOptions(repair, user).length && (
                <TableDots
                  open={openDotsMenuIndex === i}
                  onToggle={() => handleDotsToggle(i)}
                  options={getRepairOptions(repair, user)}
                  onSelect={(val) => {
                    handleDotsOptionSelect(repair, repair.container.number, val, repair.id);
                  }}
                />
              )}
            </TableIconsCell>
          ),
        },
        {
          head: () => <TableHeadCell align={'left'}>{i18n.t('label.photo')}</TableHeadCell>,
          body: (repair) => (
            <TableIconCell component={'th'} align={'left'}>
              <ContainerAvatar images={repair.beforeImages} />
            </TableIconCell>
          ),
        },
        {
          headText: () => i18n.t('common.client'),
          bodyText: (repair) => repair.container.clientYard?.client?.code,
          isHidden: () => !showToRoles('admin', user),
        },
        {
          headText: () => i18n.t('label.containerNumber'),
          bodyText: (repair) => repair.container.number,
        },
        {
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={containerId ? '' : 'createdAt'}
              sortField={containerId ? '' : sortField}
              sortDirection={containerId ? '' : sortDirection}
              handleSort={containerId ? () => null : handleSort}
            >
              {i18n.t('label.createdAt')}
            </TableHeadCell>
          ),
          bodyText: (repair) => (repair.createdAt ? momentTextFormat(repair.createdAt, user) : ''),
        },
        {
          headText: () => i18n.t('label.status'),
          body: (repair) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={RepairStatusToChipLabels[repair.status]}
                color={RepairStatusToChipColors[repair.status]}
              />
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.invoiceStatus'),
          body: (repair) => (
            <TableCell align={'left'}>
              {repair.status === 'COMPLETED' && (
                <Chip
                  size={'small'}
                  label={
                    RepairInvoiceStatusToChipLabels[repair.invoiceLineId ? 'CREATED' : 'UNCREATED']
                  }
                  color={
                    RepairInvoiceStatusToChipColors[repair.invoiceLineId ? 'CREATED' : 'UNCREATED']
                  }
                />
              )}
            </TableCell>
          ),
          isHidden: (repair) => repair.status !== 'COMPLETED',
        },
        {
          headText: () => i18n.t('label.price'),
          bodyText: (repair) =>
            repair.price ? repair.price + CURRENCIES[repair.currencyCode].symbol : '',
        },
        {
          headText: () => i18n.t('label.confirmActions'),
          body: (repair) => (
            <TableCellWrapperCenter align={'left'}>
              {repair.status === 'PENDING' && (
                <>
                  <Button
                    size={'medium'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDotsOptionSelect(
                        repair,
                        repair.container.number,
                        'client-confirm',
                        repair.id,
                      );
                    }}
                  >
                    {i18n.t('dropdown.approve')}
                  </Button>
                  <Button
                    size={'medium'}
                    data-color={'destructive'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDotsOptionSelect(
                        repair,
                        repair.container.number,
                        'client-reject',
                        repair.id,
                      );
                    }}
                  >
                    {i18n.t('dropdown.reject')}
                  </Button>
                </>
              )}
            </TableCellWrapperCenter>
          ),
          isHidden: () => !showToRoles('client', user),
        },
        {
          headText: () => i18n.t('label.daysToComplete'),
          bodyText: (repair) =>
            repair.expectedDurationDays ? repair.expectedDurationDays + ' Days' : '',
        },
        {
          headText: () => i18n.t('label.acceptedAt'),
          bodyText: (repair) =>
            repair.acceptedAt ? momentTextFormat(repair.acceptedAt, user) : '',
        },
        {
          headText: () => i18n.t('label.priceSetBy'),
          bodyText: (repair) => repair.completedByName,
        },
      ]}
      footer={() =>
        !containerId ? (
          <TableWrapperFooter>
            <Pagination
              count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
              page={page}
              onChange={handleOnPageChange}
            />
          </TableWrapperFooter>
        ) : null
      }
    />
  );
};
